import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, ArrowLeft, Minus, Plus, Check, Truck, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Funzione per estrarre le taglie dalla descrizione CJDropshipping
const extractSizesFromDescription = (description) => {
  if (!description) return [];
  
  // Pattern per trovare taglie in vari formati (inglese + italiano)
  const sizePatterns = [
    // Italiano: "Taglie: S, M, L, XL, 2XL"
    /[Tt]agli[ae]\s*:\s*([A-Z0-9][A-Za-z0-9,\s]+?)(?:<br|<\/|<p|$|\n|Nota|Genere|Stile)/i,
    // Italiano numeri: "Taglie: 34, 35, 36..."
    /[Tt]agli[ae]\s*:\s*(\d+(?:\s*,\s*\d+)+)(?:<br|<\/|<p|$|\n)/i,
    // Standard: "Sizes: S, M, L, XL, 2XL"
    /[Ss]izes?\s*:\s*([A-Z0-9][A-Za-z0-9,\s]+?)(?:<br|<\/|<p|$|\n|Note|Applicable|Gender|Fit:)/i,
    // Con pesi: "Size: M [42.5–52.5 kg], L [52.5–60 kg]"
    /[Ss]ize\s*:\s*((?:[XSML0-9]+\s*\[[^\]]+\][,\s]*)+)/i,
    // Numeri scarpe: "Size: 34, 35, 36..."
    /[Ss]ize\s*:\s*(\d+(?:\s*,\s*\d+)+)(?:<br|<\/|<p|$|\n)/i,
  ];
  
  for (const pattern of sizePatterns) {
    const match = description.match(pattern);
    if (match && match[1]) {
      let sizesStr = match[1].trim();
      
      // Evita falsi positivi come "Size & Fit"
      if (sizesStr.includes('&') || sizesStr.includes('Fit') || sizesStr.length < 2) continue;
      
      // Se contiene [kg], estrai solo le taglie
      if (sizesStr.includes('[')) {
        const sizeMatches = sizesStr.match(/([XSML0-9]+)\s*\[/g);
        if (sizeMatches) {
          const sizes = sizeMatches.map(s => s.replace(/\s*\[/, '').trim());
          if (sizes.length > 0) return sizes;
        }
      } else {
        // Formato normale: splitta per virgola
        const sizes = sizesStr
          .split(/,/)
          .map(s => s.trim())
          .filter(s => s && s.length > 0 && s.length < 10 && !s.includes('&') && !s.includes('Note') && !s.includes('Applicable') && !s.includes('Fit'));
        
        if (sizes.length > 0 && sizes.length < 20) return sizes;
      }
    }
  }
  return [];
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Estrai taglie dalla descrizione
  const availableSizes = useMemo(() => {
    if (!product?.description) return [];
    return extractSizesFromDescription(product.description);
  }, [product?.description]);

  useEffect(() => {
    // Reset state when product ID changes
    setLoading(true);
    setProduct(null);
    setQuantity(1);
    setSelectedImage(0);
    setSelectedSize(null);
    setShowFullDescription(false);
    window.scrollTo(0, 0);
    fetchProduct();
  }, [id, language]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}?lang=${language}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Prodotto non trovato');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product && product.stock > 0) {
      // Se il prodotto ha taglie, verifica che ne sia stata selezionata una
      if (availableSizes.length > 0 && !selectedSize) {
        toast.error('Seleziona una taglia', {
          description: 'Devi selezionare una taglia prima di aggiungere al carrello'
        });
        return;
      }
      
      // Aggiungi al carrello con la taglia selezionata
      const productWithSize = {
        ...product,
        selectedSize: selectedSize || null
      };
      addItem(productWithSize, quantity);
      
      const sizeInfo = selectedSize ? ` - Taglia: ${selectedSize}` : '';
      toast.success(t('addToCart'), {
        description: `${quantity}x ${product.title}${sizeInfo}`
      });
    }
  };

  const placeholderImage = 'https://images.unsplash.com/photo-1592868378269-2c502b2730ff?w=800&h=800&fit=crop';

  if (loading) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-muted rounded-3xl animate-pulse" />
            <div className="space-y-6">
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-10 bg-muted rounded w-3/4" />
              <div className="h-8 bg-muted rounded w-1/4" />
              <div className="h-32 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Prodotto non trovato</h2>
          <Link to="/products">
            <Button>{t('back')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const images = product.images?.length > 0 ? product.images : [placeholderImage];

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Back Button */}
        <Link to="/products" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('back')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-square rounded-3xl overflow-hidden bg-white border border-border">
              <img
                src={images[selectedImage]}
                alt={product.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = placeholderImage; }}
                data-testid="product-main-image"
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img
                      src={img}
                      alt={`${product.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = placeholderImage; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="lg:sticky lg:top-32 lg:self-start">
            <p className="text-sm font-medium uppercase tracking-widest text-accent mb-2">
              {product.category}
            </p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight font-serif mb-4" data-testid="product-title">
              {product.title}
            </h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-4xl font-bold text-primary" data-testid="product-price">
                €{product.price?.toFixed(2)}
              </span>
              {product.stock > 0 ? (
                <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">
                  <Check className="h-4 w-4" />
                  {t('inStock')} ({product.stock} {product.stock === 1 ? 'pezzo' : 'pezzi'})
                </span>
              ) : (
                <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  {t('outOfStock')}
                </span>
              )}
            </div>

            {/* Size Selector - Solo se il prodotto ha taglie da CJDropshipping */}
            {availableSizes.length > 0 && (
              <div className="mb-6" data-testid="size-selector">
                <span className="text-sm font-medium block mb-3">
                  {availableSizes[0].match(/^\d+$/) ? 'Numero:' : 'Taglia:'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                        selectedSize === size
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-primary hover:bg-primary/5'
                      }`}
                      data-testid={`size-option-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {!selectedSize && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ Seleziona una {availableSizes[0].match(/^\d+$/) ? 'misura' : 'taglia'} prima di aggiungere al carrello
                  </p>
                )}
              </div>
            )}

            {/* Description with "Read More" */}
            <div className="mb-8" data-testid="product-description">
              <div 
                className={`prose prose-sm text-muted-foreground overflow-hidden transition-all duration-300 ${
                  showFullDescription ? 'max-h-none' : 'max-h-32'
                }`}
                style={{ 
                  maskImage: !showFullDescription ? 'linear-gradient(to bottom, black 60%, transparent 100%)' : 'none',
                  WebkitMaskImage: !showFullDescription ? 'linear-gradient(to bottom, black 60%, transparent 100%)' : 'none'
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: product.description || '' }} />
              </div>
              {product.description && product.description.length > 200 && (
                <button
                  onClick={() => setShowFullDescription(!showFullDescription)}
                  className="mt-2 text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1 transition-colors"
                >
                  {showFullDescription ? (
                    <>Mostra meno ↑</>
                  ) : (
                    <>Leggi di più ↓</>
                  )}
                </button>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            {product.stock > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium">{t('quantity')}:</span>
                  <div className="flex items-center gap-2 bg-white border border-input rounded-full p-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      disabled={quantity <= 1}
                      data-testid="quantity-minus"
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-12 text-center font-medium" data-testid="quantity-value">{quantity}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                      disabled={quantity >= product.stock}
                      data-testid="quantity-plus"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <Button
                  className="w-full btn-primary"
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {t('addToCart')} - €{(product.price * quantity).toFixed(2)}
                </Button>
              </div>
            )}

            {/* Features */}
            <div className="mt-8 pt-8 border-t border-border space-y-4">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Truck className="h-5 w-5 text-primary" />
                <span>Spedizione gratuita per ordini sopra €50</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Shield className="h-5 w-5 text-primary" />
                <span>Pagamento sicuro con PayPal</span>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewsSection productId={id} />
      </div>
    </div>
  );
};

// Reviews Component
const ReviewsSection = ({ productId }) => {
  const [reviews, setReviews] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchReviews();
  }, [productId]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <svg 
        key={i} 
        className={`h-4 w-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-neutral-200 fill-neutral-200'}`}
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const flagEmoji = {
    it: '🇮🇹',
    en: '🇬🇧',
    es: '🇪🇸',
    fr: '🇫🇷'
  };

  if (loading) {
    return (
      <div className="mt-16 pt-16 border-t border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48" />
          <div className="h-24 bg-muted rounded" />
          <div className="h-24 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-16 pt-16 border-t border-neutral-200" data-testid="reviews-section">
      <div className="max-w-4xl">
        <h2 className="text-2xl font-light mb-8">
          Recensioni dei Clienti
          <span className="text-sm text-neutral-400 ml-3">({reviews.length})</span>
        </h2>
        
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="pb-6 border-b border-neutral-100 last:border-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {renderStars(review.rating)}
                  </div>
                  <span className="text-sm font-medium">{review.author}</span>
                  {review.verified && (
                    <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                      Acquisto verificato
                    </span>
                  )}
                  <span className="text-sm">{flagEmoji[review.language] || '🌍'}</span>
                </div>
                <span className="text-xs text-neutral-400">{review.date}</span>
              </div>
              <p className="text-sm text-neutral-600 leading-relaxed">
                {review.text}
              </p>
              {review.helpful_count > 0 && (
                <p className="text-xs text-neutral-400 mt-2">
                  {review.helpful_count} persone hanno trovato utile questa recensione
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
